'use client';

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Mail, Phone, MapPin, Send, MessageSquare, User, Tag, Info, CheckCircle2, AlertCircle, Building2, Clock } from 'lucide-react';
import Footer from '@/components/Footer';


export default function ContatoPage() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(false);

    const formData = new FormData(e.currentTarget);
    
    try {
      const response = await fetch("https://formsubmit.co/ajax/contato@estudiomidia.com.br", {
        method: "POST",
        body: formData,
        headers: {
          'Accept': 'application/json'
        }
      });

      if (response.ok) {
        setSubmitted(true);
        e.currentTarget.reset();
      } else {
        setError(true);
      }
    } catch (err) {
      setError(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-900 selection:bg-blue-200 selection:text-blue-900">
      {/* Header */}
      <header className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white shadow-lg">
        <div className="max-w-4xl mx-auto px-4 py-12 md:py-20 text-center space-y-6">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 backdrop-blur-sm border border-white/30 px-6 py-3 rounded-2xl text-white font-black uppercase tracking-tighter italic transition-all hover:-translate-x-1 active:scale-95 mb-8 group shadow-lg"
          >
            <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
            Voltar para o Início
          </Link>
          <h1 className="text-3xl md:text-5xl font-black tracking-tighter uppercase italic">
            Fale Conosco <br className="md:hidden" />
            <span className="text-blue-200">Catanduvas PR</span>
          </h1>
          <p className="text-blue-100 text-lg md:text-xl font-medium max-w-2xl mx-auto">
            Dúvidas, sugestões, anúncios ou reporte de erros? Nossa equipe está pronta para te ouvir.
          </p>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 -mt-8 pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          
          {/* Informações de Contato */}
          <div className="lg:col-span-1 space-y-6">
            <div className="bg-white rounded-[2.5rem] p-8 shadow-2xl border border-slate-100 space-y-8">
              <div className="space-y-4">
                <h2 className="text-2xl font-black text-slate-800 uppercase tracking-tighter italic">Canais Diretos</h2>
                <p className="text-slate-500 font-bold text-sm leading-relaxed">
                  Utilize nossos canais oficiais para um atendimento mais rápido.
                </p>
              </div>

              <div className="space-y-6">
                <div className="flex items-center gap-4 group">
                  <div className="bg-blue-100 p-4 rounded-2xl text-blue-600 group-hover:bg-blue-600 group-hover:text-white transition-all shadow-sm">
                    <Mail className="w-6 h-6" />
                  </div>
                  <div>
                    <p className="text-xs font-black uppercase tracking-widest text-slate-400">E-mail Oficial</p>
                    <p className="text-lg font-black text-slate-800 tracking-tighter italic">contato@estudiomidia.com.br</p>
                  </div>
                </div>

                <div className="flex items-center gap-4 group">
                  <div className="bg-emerald-100 p-4 rounded-2xl text-emerald-600 group-hover:bg-emerald-600 group-hover:text-white transition-all shadow-sm">
                    <Phone className="w-6 h-6" />
                  </div>
                  <div>
                    <p className="text-xs font-black uppercase tracking-widest text-slate-400">WhatsApp Comercial</p>
                    <p className="text-lg font-black text-slate-800 tracking-tighter italic">(45) 99147-9671</p>
                  </div>
                </div>

                <div className="flex items-center gap-4 group">
                  <div className="bg-indigo-100 p-4 rounded-2xl text-indigo-600 group-hover:bg-indigo-600 group-hover:text-white transition-all shadow-sm">
                    <Building2 className="w-6 h-6" />
                  </div>
                  <div>
                    <p className="text-xs font-black uppercase tracking-widest text-slate-400">Administração</p>
                    <p className="text-lg font-black text-slate-800 tracking-tighter italic">Estúdio Mídia Publicidades</p>
                  </div>
                </div>
              </div>

              <div className="pt-8 border-t border-slate-100">
                <div className="bg-slate-50 p-6 rounded-3xl space-y-3">
                  <h3 className="font-black text-slate-800 uppercase tracking-tighter italic flex items-center gap-2">
                    <Clock className="w-5 h-5 text-blue-600" />
                    Tempo de Resposta
                  </h3>
                  <p className="text-slate-500 text-sm font-medium leading-relaxed">
                    Nossa equipe analisa todas as mensagens em até <strong>24 horas úteis</strong>.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Formulário de Contato */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-[2.5rem] p-8 md:p-12 shadow-2xl border border-slate-100">
              {submitted ? (
                <div className="text-center py-20 space-y-6">
                  <div className="bg-emerald-100 w-24 h-24 rounded-full flex items-center justify-center mx-auto text-emerald-600 animate-bounce">
                    <CheckCircle2 className="w-12 h-12" />
                  </div>
                  <h2 className="text-3xl font-black text-slate-800 uppercase tracking-tighter italic">Mensagem Enviada!</h2>
                  <p className="text-slate-500 font-bold max-w-md mx-auto">
                    Obrigado pelo seu contato. Nossa equipe recebeu sua mensagem e responderá em breve no e-mail informado.
                  </p>
                  <button 
                    onClick={() => setSubmitted(false)}
                    className="bg-blue-600 text-white px-8 py-4 rounded-2xl font-black uppercase tracking-tighter italic transition-all hover:scale-105 active:scale-95 shadow-lg shadow-blue-200"
                  >
                    Enviar Nova Mensagem
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-8">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Nome */}
                    <div className="space-y-2">
                      <label className="text-xs font-black uppercase tracking-widest text-slate-400 ml-4 flex items-center gap-2">
                        <User className="w-3 h-3" /> Seu Nome Completo
                      </label>
                      <input 
                        required
                        type="text" 
                        name="name"
                        placeholder="Ex: João da Silva"
                        className="w-full bg-slate-50 border-2 border-transparent focus:border-blue-600 focus:bg-white rounded-2xl px-6 py-4 outline-none transition-all font-bold text-slate-800"
                      />
                    </div>

                    {/* E-mail */}
                    <div className="space-y-2">
                      <label className="text-xs font-black uppercase tracking-widest text-slate-400 ml-4 flex items-center gap-2">
                        <Mail className="w-3 h-3" /> Seu Melhor E-mail
                      </label>
                      <input 
                        required
                        type="email" 
                        name="email"
                        placeholder="Ex: joao@email.com"
                        className="w-full bg-slate-50 border-2 border-transparent focus:border-blue-600 focus:bg-white rounded-2xl px-6 py-4 outline-none transition-all font-bold text-slate-800"
                      />
                    </div>

                    {/* Telefone */}
                    <div className="space-y-2">
                      <label className="text-xs font-black uppercase tracking-widest text-slate-400 ml-4 flex items-center gap-2">
                        <Phone className="w-3 h-3" /> Telefone / WhatsApp
                      </label>
                      <input 
                        required
                        type="tel" 
                        name="phone"
                        placeholder="Ex: (45) 99999-9999"
                        className="w-full bg-slate-50 border-2 border-transparent focus:border-blue-600 focus:bg-white rounded-2xl px-6 py-4 outline-none transition-all font-bold text-slate-800"
                      />
                    </div>

                    {/* Assunto */}
                    <div className="space-y-2">
                      <label className="text-xs font-black uppercase tracking-widest text-slate-400 ml-4 flex items-center gap-2">
                        <Tag className="w-3 h-3" /> Assunto do Contato
                      </label>
                      <select 
                        required
                        name="subject"
                        className="w-full bg-slate-50 border-2 border-transparent focus:border-blue-600 focus:bg-white rounded-2xl px-6 py-4 outline-none transition-all font-bold text-slate-800 appearance-none cursor-pointer"
                      >
                        <option value="">Selecione uma opção</option>
                        <option value="Sugestão">Sugestão de Conteúdo</option>
                        <option value="Anúncio">Quero Anunciar no Portal</option>
                        <option value="Erro">Reportar Erro no Site</option>
                        <option value="Dúvida">Dúvida Geral</option>
                        <option value="Outro">Outros Assuntos</option>
                      </select>
                    </div>
                  </div>

                  {/* Mensagem */}
                  <div className="space-y-2">
                    <label className="text-xs font-black uppercase tracking-widest text-slate-400 ml-4 flex items-center gap-2">
                      <MessageSquare className="w-3 h-3" /> Sua Mensagem Detalhada
                    </label>
                    <textarea 
                      required
                      name="message"
                      rows={6}
                      placeholder="Escreva aqui sua mensagem com o máximo de detalhes possível..."
                      className="w-full bg-slate-50 border-2 border-transparent focus:border-blue-600 focus:bg-white rounded-[2rem] px-6 py-6 outline-none transition-all font-bold text-slate-800 resize-none"
                    ></textarea>
                  </div>

                  {/* Anti-Spam (FormSubmit feature) */}
                  <input type="hidden" name="_captcha" value="false" />
                  <input type="hidden" name="_subject" value="Novo Contato - CatanduvasPR.com.br" />
                  <input type="hidden" name="_template" value="table" />

                  {error && (
                    <div className="bg-red-50 text-red-600 p-4 rounded-2xl flex items-center gap-3 font-bold text-sm">
                      <AlertCircle className="w-5 h-5" />
                      Ocorreu um erro ao enviar. Tente novamente ou use o WhatsApp.
                    </div>
                  )}

                  <button 
                    disabled={isSubmitting}
                    type="submit"
                    className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 text-white py-6 rounded-[2rem] font-black uppercase tracking-widest italic flex items-center justify-center gap-3 transition-all hover:scale-[1.02] active:scale-95 shadow-xl shadow-blue-200"
                  >
                    {isSubmitting ? (
                      <div className="w-6 h-6 border-4 border-white/30 border-t-white rounded-full animate-spin"></div>
                    ) : (
                      <>
                        <Send className="w-6 h-6" />
                        Enviar Mensagem Agora
                      </>
                    )}
                  </button>

                  <p className="text-center text-xs text-slate-400 font-medium">
                    Ao enviar este formulário, você concorda com nossos <Link to="/termos" className="underline">Termos de Uso</Link> e <Link to="/privacidade" className="underline">Política de Privacidade</Link>.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
